import { STATE_FORMAT, getRegionCode } from "../formats/state";
import { POSTAL_CODE_FORMAT } from "../formats/postal-code";

export const CANADIAN_POSTAL_CODE_ERROR =
  "Enter a Canadian postal code, such as M5X 1A9.";

export function isCanadianPostalCode(value: string): boolean {
  return /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTVWXYZ][ -]?\d[ABCEGHJ-NPRSTVWXYZ]\d$/i.test(
    value.trim(),
  );
}

export interface AddressLike {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
}

const ADDRESS_PLACEHOLDER_STRINGS = new Set([
  "address not specified",
  "unknown",
  "n/a",
  "na",
  "-",
  "--",
]);

export function isAddressPlaceholder(value: string): boolean {
  return ADDRESS_PLACEHOLDER_STRINGS.has(value.trim().toLowerCase());
}

export function isAddressFieldBlank(value: string | null | undefined): boolean {
  if (!value) return true;
  const trimmed = value.trim();
  if (trimmed.length === 0) return true;
  return isAddressPlaceholder(trimmed);
}

export function isAddressComplete(address: AddressLike | null | undefined) {
  if (!address) return false;
  return (
    !isAddressFieldBlank(address.line1) &&
    !isAddressFieldBlank(address.city) &&
    !isAddressFieldBlank(address.state) &&
    !isAddressFieldBlank(address.postalCode) &&
    !isAddressFieldBlank(address.country)
  );
}

export function getRequestDeliveryRegion(
  address: AddressLike | null | undefined,
) {
  const enteredState = address?.state?.trim() ?? "";
  const state = getRegionCode(enteredState) ?? enteredState.toUpperCase();
  const country =
    address?.country?.trim().toUpperCase() ||
    (STATE_FORMAT.isCAProvince(state) ? "CA" : "US");
  return { state, country };
}

export function getRequestDeliveryAddressErrors(
  address: AddressLike | null | undefined,
): { state?: string; postalCode?: string } {
  const { state, country } = getRequestDeliveryRegion(address);
  const errors: { state?: string; postalCode?: string } = {};
  if (country !== "US" && country !== "CA") {
    errors.state = "Choose a state or province in the US or Canada.";
    return errors;
  }
  if (state && !STATE_FORMAT.validate(state, country)) {
    errors.state =
      country === "CA"
        ? "Choose a Canadian province or territory."
        : "Choose a US state or territory.";
  }
  const postalCode = address?.postalCode?.trim() ?? "";
  if (postalCode && country === "CA" && !isCanadianPostalCode(postalCode)) {
    errors.postalCode = CANADIAN_POSTAL_CODE_ERROR;
  }
  if (
    postalCode &&
    country === "US" &&
    !POSTAL_CODE_FORMAT.pattern.test(postalCode) &&
    !POSTAL_CODE_FORMAT.extendedPattern.test(postalCode)
  ) {
    errors.postalCode =
      "Enter a 5-digit US ZIP code or ZIP+4, such as 75001 or 75001-1234.";
  }
  return errors;
}

export function isRequestDeliveryAddressComplete(
  address: AddressLike | null | undefined,
): boolean {
  return (
    isAddressComplete({ ...address, ...getRequestDeliveryRegion(address) }) &&
    Object.keys(getRequestDeliveryAddressErrors(address)).length === 0
  );
}
